import React, { useState } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Paper
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

/**
 * Inline CAMO QM creation acknowledgment form (used in review tab and dialog).
 */
const CamoQmAcknowledgmentForm = ({ workOrder, onSuccess, onCancel, embedded = false }) => {
  const [decision, setDecision] = useState('ACKNOWLEDGE');
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const cleanedRemarks = remarks.trim();
      const cleanedNotes = notes.trim();
      if (decision === 'REJECT' && !cleanedRemarks) {
        const message = 'Remarks are required when rejecting a work order.';
        setError(message);
        showToast(message, 'error');
        setLoading(false);
        return;
      }

      await axios.post(`/api/maintenance/${workOrder._id}/camo-qm/acknowledge`, {
        decision,
        notes: cleanedNotes || undefined,
        remarks: cleanedRemarks || undefined
      });

      showToast(
        decision === 'REJECT' ? 'Work order rejected successfully' : 'Work order acknowledged successfully',
        'success'
      );
      onSuccess?.();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to acknowledge work order';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const Wrapper = embedded ? Paper : Box;

  return (
    <Wrapper variant={embedded ? 'outlined' : undefined} sx={{ p: embedded ? 2 : 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        CAMO QM Acknowledgment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review the work order details above, then acknowledge or reject creation.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Decision
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={decision}
          onChange={(_, value) => {
            if (value) setDecision(value);
          }}
          size="small"
        >
          <ToggleButton value="ACKNOWLEDGE">Acknowledge</ToggleButton>
          <ToggleButton value="REJECT" color="error">Reject</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes or comments about this work order..."
        sx={{ mb: 2 }}
      />

      {decision === 'REJECT' && (
        <TextField
          fullWidth
          required
          multiline
          rows={3}
          label="Rejection Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Explain why the work order is rejected and what needs correction..."
          sx={{ mb: 2 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={decision === 'REJECT' ? 'error' : 'primary'}
          disabled={loading}
        >
          {loading ? 'Submitting...' : (decision === 'REJECT' ? 'Reject Work Order' : 'Acknowledge Work Order')}
        </Button>
      </Box>
    </Wrapper>
  );
};

export default CamoQmAcknowledgmentForm;
